/**
 * Sync History — admin
 *
 * The permanent record of background runs, across every retailer account.
 *
 * WHY IT IS ITS OWN PAGE. It exists because most of the connector's work is NOT brand work.
 * Fetching a retailer's orders, refreshing its SKU map, sending a stock import and comparing
 * stock all cover every brand on the account at once, so they record a retailer and no brand
 * — and the brand page, which selects by brand, was the console's only reader of this table.
 * Those runs were written permanently and shown nowhere. Pairing it with the Queue in the
 * sidebar keeps that pair together: the queue is live and ages out, this does not.
 *
 * No brandId is passed, which is what selects the connector-wide view — the brand-scoped
 * placement of the same component merges billing's runs in and adds a Source column.
 */

import { useEffect, useState } from 'react';
import { Page, Layout, Card, Text, Box, BlockStack, InlineStack } from '@shopify/polaris';
import SyncHistoryTable from '../components/SyncHistoryTable';
import SharedInfoTip from '../components/InfoTip';
import apiClient from '../utils/api';

export default function BackgroundSyncHistory() {
  const [retailers, setRetailers] = useState([]);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const data = await apiClient.getConnectorMarketplaces();
        if (live) setRetailers(data || []);
      } catch {
        // Silent for the same reason as the Queue page: this list only populates the retailer
        // filter. SyncHistoryTable loads the runs itself and keeps whatever rows it has on a
        // failed refresh, so a banner here would report a problem the table does not have.
      }
    })();
    return () => { live = false; };
  }, []);

  // SyncHistoryTable builds its own Select from the accounts, so it wants {id, name} — not
  // the {label, value} options QueueTable expects.
  const marketplaces = retailers.map((m) => ({ id: m.id, name: m.name }));

  return (
    <Page fullWidth>
      <Layout>
        <Layout.Section>
          <BlockStack gap="050">
            <InlineStack gap="100" blockAlign="center">
              <Text variant="headingLg" as="h1">Sync History</Text>
              <SharedInfoTip content="The permanent record of background runs, kept in the database rather than the queue — so unlike the Queue page it does not age out. A job switched off in Sync Settings completes without writing a row here, which is why the queue can show work this table does not." />
            </InlineStack>
          </BlockStack>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <Box padding="400">
              <SyncHistoryTable
                marketplaces={marketplaces}
                emptyMessage="Nothing has run yet. Each row will be one run — what it did, what it got through, and why it stopped if it failed."
              />
            </Box>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
